import { TaskWidgetMappingRegistry } from './TaskWidgetMapping';
import { NaturalLanguageProcessor } from './NLPProcessor';
import { WidgetManager } from './WidgetManager';
import { outOfScopeText } from './DemoIntentService';
import { chatDemoCopy, getChatLanguage, type ChatDemoLanguage } from '@/lib/i18n/chat-demo-copy';

export interface ConversationRequest {
  userQuery: string;
  context: {
    persona?: string;
    manufacturingType?: string;
    businessOutcome?: string;
    userId?: string;
    companyId?: string;
  };
  sessionId: string;
  timestamp: Date;
}

export interface ConversationResponse {
  conversationalText: string;
  widget?: {
    type: string;
    data: any;
    actions: string[];
  };
  suggestedActions: Array<{
    actionType: string;
    label: string;
    priority: number;
  }>;
  confidence: number;
  agentResponses: Record<string, any>;
}

export class ConversationalInterfaceEngine {
  private taskWidgetMapping: TaskWidgetMappingRegistry;
  private nlpProcessor: NaturalLanguageProcessor;
  private widgetManager: WidgetManager;

  constructor() {
    this.taskWidgetMapping = new TaskWidgetMappingRegistry();
    this.nlpProcessor = new NaturalLanguageProcessor();
    this.widgetManager = new WidgetManager();
  }

  /**
   * NOTE (BUG-108): there used to be a Step 3 here — `AgentOrchestrator
   * .orchestrate()`. That module was 306 lines of mock adapters, self-described
   * at its own `:183` as "Mock agent adapters - these would connect to your
   * actual agent services", returning a hardcoded `currentBalance: 2400000`
   * with an invented named-account breakdown (Operating Account $1.2M, Reserve
   * Fund $800K, Investment Account $400K) and per-agent hardcoded confidences.
   * It was tenant-independent by construction, so it produced the same balance
   * for every company. It has been deleted, not rewired: there is no real
   * agent-service layer for it to orchestrate, and a shell returning empty data
   * with invented confidences is the BUG-104 / BUG-105 defect in a new costume.
   *
   * `agentResponses` is therefore `{}` on every response this class returns.
   * That is the honest value — no agent ran — and it is what stops the answer
   * naming supporting agents (see the BUG-104 invariant test).
   *
   * Two stacked crashes used to hide all of this, and both are fixed here:
   *   1. `WidgetManager.executeWidget` threw on `widget.operations` (BUG-96).
   *   2. `generateConversationalResponse` built all five templates EAGERLY in
   *      one object literal, so `widgetData.recommendations.length` threw even
   *      for a cash-position query. Fixing only (1) just moved the crash.
   * Both surfaced to the user as "I encountered an issue processing your
   * request" (BUG-110), which is why one bug looked like three.
   */
  async processConversation(request: ConversationRequest): Promise<ConversationResponse> {
    const lang = getChatLanguage();

    try {
      // Step 1: Parse the natural language query
      const parsedQuery = await this.nlpProcessor.parse(request.userQuery);

      // Step 2: Identify the appropriate task and widget
      const taskMapping = this.taskWidgetMapping.getMapping(parsedQuery.intent);

      if (!taskMapping) {
        return this.generateCapabilityResponse(lang);
      }

      // Step 3: Execute the widget against its real endpoint. `null` means it
      // could not run — unknown operation, an input we cannot supply, or a
      // failed request. There is no fallback data to substitute.
      const widgetData = await this.widgetManager.executeWidget(taskMapping.widget, taskMapping.actions);

      if (!widgetData) {
        return this.generateCapabilityResponse(lang);
      }

      // Step 4: Describe what came back — only if it is genuinely there.
      const conversationalText = this.generateConversationalResponse(parsedQuery.intent, widgetData);

      if (!conversationalText) {
        return this.generateCapabilityResponse(lang);
      }

      return {
        conversationalText,
        widget: {
          type: taskMapping.widget.type,
          data: widgetData,
          actions: taskMapping.actions.map(a => a.actionType)
        },
        suggestedActions: taskMapping.actions,
        // The NLP match strength — genuinely computed from the query. It is not
        // an agent confidence, because no agent ran.
        confidence: parsedQuery.confidence,
        agentResponses: {}
      };

    } catch (error) {
      console.error('Conversational processing error:', error);
      return this.generateErrorResponse(lang);
    }
  }

  /**
   * Describe the widget payload, or return `null` if it cannot be described
   * truthfully.
   *
   * Every template states a figure, so every template first checks that the
   * figure is actually present. A missing field yields `null` (the caller then
   * says what it CAN answer) rather than interpolating `undefined` or `NaN`
   * into a sentence about the user's money.
   *
   * These strings are English-only. That is pre-existing and deliberately not
   * expanded here: none of them can render today, because the endpoints they
   * describe (`/api/functions/v1/cash-position` and friends) do not exist in
   * the Hono API. Whoever builds those endpoints owns translating these; the
   * path a user actually reaches — `generateCapabilityResponse` — is bilingual.
   */
  private generateConversationalResponse(intent: string, widgetData: Record<string, any>): string | null {
    const present = (...keys: string[]) =>
      keys.every((k) => widgetData[k] !== undefined && widgetData[k] !== null && widgetData[k] !== '');
    const counted = (key: string) => Array.isArray(widgetData[key]);

    switch (intent) {
      case 'cash-position':
        return present('currentBalance', 'opportunities')
          ? `Here's your current cash position. You have ${widgetData.currentBalance} available, with ${widgetData.opportunities} in optimization opportunities.`
          : null;

      case 'payment-acceleration':
        return counted('opportunities') && present('totalOpportunity')
          ? `I found ${widgetData.opportunities.length} invoices ready for acceleration, potentially bringing in ${widgetData.totalOpportunity} faster.`
          : null;

      case 'vendor-payments':
        return counted('recommendations')
          ? `Based on your cash flow, I recommend paying ${widgetData.recommendations.length} vendors this week to optimize your working capital.`
          : null;

      case 'contract-qualification':
        return present('strengthScore', 'qualificationLimit')
          ? `Your financial strength score is ${widgetData.strengthScore}. You qualify for contracts up to ${widgetData.qualificationLimit}.`
          : null;

      case 'cross-border':
        return present('exposure', 'potentialSavings')
          ? `Your international exposure is ${widgetData.exposure}. I can help optimize FX costs by ${widgetData.potentialSavings}.`
          : null;

      default:
        return null;
    }
  }

  /**
   * BUG-110 — what a financial question outside the four Phase-1 intents gets.
   *
   * It used to be `I'm not sure how to help with "<query>"` (this class's
   * `generateFallbackResponse`) or `I encountered an issue processing your
   * request` (its error path). Two strings, two code paths, one experience: a
   * dead end, from a product whose promise is answering financial questions.
   *
   * `outOfScopeText` is the same bilingual capability message the Phase-1
   * router already returns for an unrecognised query, so there is ONE answer to
   * "what can you actually do" instead of three divergent copies. It names the
   * four intents that genuinely work. It invents nothing.
   */
  private generateCapabilityResponse(lang: ChatDemoLanguage): ConversationResponse {
    return {
      conversationalText: outOfScopeText(lang),
      suggestedActions: [],
      confidence: 0,
      agentResponses: {}
    };
  }

  /**
   * Reserved for a genuinely unexpected exception — kept distinct from the
   * capability message on purpose. "I could not answer that, here is what I
   * can answer" and "something broke" are different facts, and BUG-110 was
   * partly mis-diagnosed because the two were read as one symptom.
   */
  private generateErrorResponse(lang: ChatDemoLanguage): ConversationResponse {
    return {
      conversationalText: chatDemoCopy(lang).errorResponse,
      suggestedActions: [
        { actionType: 'retry', label: 'Try Again', priority: 1 },
        { actionType: 'support', label: 'Contact Support', priority: 2 }
      ],
      confidence: 0,
      agentResponses: {}
    };
  }
}